import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import { TranslateModule, _ } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { FilterComponent } from 'src/app/components/filters/filter.component';
import { FilterVariantComponent } from 'src/app/components/filters/forms/filter-variant/filter-variant.component';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { SelectComponent } from 'src/app/components/select/select.component';
import { TranslateService } from 'src/app/services/translate.service';
import { SizeGuideCreateModalComponent } from '../../../../modals/size-guide-create-modal/size-guide-create-modal.component';
import { Category } from '../../../../models/category';
import { CreateOfferData } from '../../../../models/create-offer/create-offer-data';
import { FilterData, FilterForm, ManualVariantSelection } from '../../../../models/create-offer/filter-data';
import { CompositionFilterValue, Filter } from '../../../../models/filter';
import { Media } from '../../../../models/media';
import { OfferVariantGroup } from '../../../../models/offer';
import { OfferVariantPost, OfferVariantValuePost, PostOfferVariantDefinition } from '../../../../models/offer-definition';
import { AppPlatform } from '../../../../models/platform';
import { SizeGuide } from '../../../../models/size-guide';
import { User } from '../../../../models/user';
import { AuthService } from '../../../../services/auth.service';
import { FilterService } from '../../../../services/filter.service';
import { MediaService } from '../../../../services/media.service';
import { SizeGuideService } from '../../../../services/size-guide.service';
import { AlertUtils } from '../../../../utils/alert.utils';
import { CategoryUtils } from '../../../../utils/category.utils';
import {
    compositionGroupKey,
    compositionGroupLabel,
    compositionGroupTotal,
    groupCompositionFilters,
    isCompositionFormValue,
    listCompositionLineKeys,
} from '../../../../utils/composition-group.utils';
import {
    alignWizardMediasWithGroups,
    attachEditMediasToOfferGroup,
    copyVariantGroupMedias,
    keepPricedVariants,
} from '../../../../utils/create-offer-wizard-restore.utils';
import { copySourceVariantIds } from '../../../../utils/offer-payload.utils';
import { OfferUtils } from '../../../../utils/offer.utils';
import { OneVariantValidator, VariantLengthValidator } from '../../../../validators/variant.validator';
import { OfferGroupVariantSelectorComponent } from './components/offer-group-variant-selector/offer-group-variant-selector.component';

/** Spaces do not count toward the 5-character minimum. maxLength still counts them. */
function titleHasTooFewCharacters(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value : '';
    if (value.replace(/\s/g, '').length < 5) {
        return { titleTooShort: true };
    }
    return null;
}

interface VariantGenerationResult {
    variants: OfferVariantPost[];
    variantGroups: OfferVariantGroup[];
    totalCount: number;
    isValid: boolean;
    errorMessage?: string;
}

@Component({
    selector: 'app-pick-filters',
    templateUrl: './pick-filters.component.html',
    styleUrls: ['./pick-filters.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        FilterComponent,
        LoaderComponent,
        SelectComponent,
        FilterVariantComponent,
        OfferGroupVariantSelectorComponent,
    ],
})
export class PickFiltersComponent implements OnInit {
    @Output() nextStep = new EventEmitter<CreateOfferData>();
    @Input() data: CreateOfferData;
    @Input() isEdit = false;
    protected readonly TITLE_MAX_LENGTH = 50;
    protected form: FormGroup<FilterForm> = new FormGroup({
        name: new FormControl('', [Validators.required, titleHasTooFewCharacters, Validators.maxLength(this.TITLE_MAX_LENGTH)]),
        description: new FormControl(''),
        filters: new FormGroup({}),
        variants: new FormGroup({}, [VariantLengthValidator]),
    });
    protected filters: Filter[] = [];
    protected displayFilters: Filter[] = [];
    protected variantFilters: Filter[] = [];
    protected filtersLoading = false;
    protected isSecondHand = false;
    protected shippable = false;
    private category: Category;
    private platform: AppPlatform;
    protected user: User;
    protected sizeGuideList: { label: string; value: string }[];
    protected variantGroups: OfferVariantGroup[] = [];
    protected offerCanHaveVariants: boolean;
    protected manualVariantResult: VariantGenerationResult = null;
    protected manualVariantSelection: ManualVariantSelection | null = null;
    private manualVariantSelectionTouched = false;
    protected isUsingManualVariantSelection = false;
    @ViewChild(OfferGroupVariantSelectorComponent)
    private offerGroupVariantSelector?: OfferGroupVariantSelectorComponent;
    protected showSalesMandateBlock = false;
    protected salesMandateMedia: Media | null = null;
    protected salesMandateUploading = false;
    protected salesMandateTouched = false;

    /** When true, a sales mandate PDF must be attached before leaving this step (matches backend rule). */
    protected get salesMandateRequired(): boolean {
        return this.showSalesMandateBlock;
    }

    /** Current title length using the same `string.length` rule as the maxLength validator. */
    protected get titleLength(): number {
        return this.form.controls.name.value?.length ?? 0;
    }

    private static readonly SALES_MANDATE_DOC_TYPE = 'sales_mandate';

    protected readonly compositionGroupTotalKey = _('composition_group_total');
    protected readonly compositionGroupInvalidKey = _('composition_group_invalid');

    constructor(
        private readonly filterService: FilterService,
        private readonly authService: AuthService,
        private readonly sizeGuideService: SizeGuideService,
        private readonly translateService: TranslateService,
        private readonly modalCtrl: ModalController,
        private readonly mediaService: MediaService,
    ) {}

    ngOnInit() {
        this.platform = this.data.platformData.platform;
        this.category = this.data.categoryData.category;
        this.shippable = CategoryUtils.findProperty(this.category, 'shippable');
        this.isSecondHand = OfferUtils.isSecondHand(this.platform, this.category);
        this.offerCanHaveVariants = OfferUtils.canHaveVariants(this.platform);
        this.updateShowSalesMandateBlock();
        this.loadSalesMandateFromFilterData();

        // Get user
        this.authService.userLoggedIn.pipe(filter((user) => !!user)).subscribe({
            next: (user: User) => {
                this.user = user;
                this.updateShowSalesMandateBlock();
                // Load filters
                this.loadFilters();
                // Enable sizeguide
                if (CategoryUtils.hasTag(this.category, 'sizeguide') && this.user.pro) {
                    this.enableSizeGuide();
                } else {
                    this.disableSizeGuide();
                }
            },
        });
        this.form.get('filters').addValidators(this.validateCompositionGroups);

        // Listen on change on filters
        this.form.get('filters').valueChanges.subscribe({
            next: (values: { [key: string]: unknown }) => {
                const allValues = {
                    ...values,
                    ...this.form.get('variants').value,
                };
                this.organizeFilters(null, allValues);
            },
        });

        // Listen on change on variants
        this.form.get('variants').valueChanges.subscribe({
            next: (values: { [key: string]: unknown }) => {
                const allValues = {
                    ...values,
                    ...this.form.get('filters').value,
                };
                this.organizeFilters(null, allValues);
            },
        });
    }

    private updateShowSalesMandateBlock(): void {
        this.showSalesMandateBlock = !!this.user?.pro && CategoryUtils.hasTag(this.category, 'real_estate_sale');
    }

    private loadSalesMandateFromFilterData(): void {
        const mandateEntry = this.data?.filterData?.documents?.find((d) => d.type === PickFiltersComponent.SALES_MANDATE_DOC_TYPE);
        const id = mandateEntry?.mediaId;
        if (!id) {
            this.salesMandateMedia = null;
            return;
        }
        firstValueFrom(this.mediaService.get(id))
            .then((m) => {
                this.salesMandateMedia = m;
            })
            .catch(() => {
                this.salesMandateMedia = new Media({ id, path: '' });
            });
    }

    async uploadSalesMandate(): Promise<void> {
        if (this.salesMandateUploading) {
            return;
        }
        this.salesMandateUploading = true;
        try {
            const media = await this.mediaService.pickDocument('document');
            if (!media) {
                return;
            }
            this.salesMandateMedia = media;
            const withoutMandate = (this.data.filterData.documents ?? []).filter(
                (d) => d.type !== PickFiltersComponent.SALES_MANDATE_DOC_TYPE,
            );
            this.data.filterData.documents = [...withoutMandate, { type: PickFiltersComponent.SALES_MANDATE_DOC_TYPE, mediaId: media.id }];
        } catch {
            await AlertUtils.showError(_('create_offer_sales_mandate_upload_error'));
        } finally {
            this.salesMandateUploading = false;
        }
    }

    removeSalesMandate(): void {
        this.salesMandateMedia = null;
        if (this.data.filterData.documents?.length) {
            this.data.filterData.documents = this.data.filterData.documents.filter(
                (d) => d.type !== PickFiltersComponent.SALES_MANDATE_DOC_TYPE,
            );
        }
    }

    /** Preserves non-mandate entries from a previous filterData when rebuilding on submit. */
    private mergeOfferDocumentsForSubmit(): { type: string; mediaId: number; expiresAt?: string | null }[] {
        const prev = (this.data.filterData?.documents ?? []).filter((d) => d.type !== PickFiltersComponent.SALES_MANDATE_DOC_TYPE);
        if (this.salesMandateMedia) {
            return [...prev, { type: PickFiltersComponent.SALES_MANDATE_DOC_TYPE, mediaId: this.salesMandateMedia.id }];
        }
        return prev;
    }

    private loadFilters() {
        this.filtersLoading = true;
        this.filterService.getFilters({ category: this.category.id, platform: this.platform }).subscribe({
            next: (filters: Filter[]) => {
                this.organizeFilters(filters);
                // Update filter and variant forms
                this.updateFilterVariantForms();
                this.seedFromExistingFilterData();
            },
        });
    }

    /**
     * When reusing this step for offer edit, filterData is pre-filled; patch the form once controls exist.
     */
    private seedFromExistingFilterData(): void {
        const fd = this.data?.filterData;
        if (!fd?.filters?.length) {
            return;
        }
        if (fd.name != null) {
            this.form.patchValue({ name: fd.name, description: fd.description ?? '' });
        }
        if (fd.sizeGuide != null && this.form.contains('sizeGuide')) {
            this.form.get('sizeGuide')?.setValue(['' + fd.sizeGuide]);
        }

        // Organize BEFORE patching using the target values, so conditional
        // controls are already created by the time we patchValue().
        const targetValues = { ...(fd.filterValues ?? {}), ...(fd.filterVariantValues ?? {}) };
        this.organizeFilters(null, targetValues);

        const filtersGroup = this.form.get('filters') as FormGroup;
        const variantsGroup = this.form.get('variants') as FormGroup;
        if (fd.filterValues) {
            for (const [code, val] of Object.entries(fd.filterValues)) {
                if (!filtersGroup.contains(code)) {
                    continue;
                }
                const control = filtersGroup.get(code);
                const filter = this.filters.find((f) => f.getCode() === code);
                // patchValue does not create child controls; rebuild composition lines first.
                if (filter?.type === 'composition' && control instanceof FormGroup && isCompositionFormValue(val)) {
                    this.seedCompositionLineControls(control, val);
                }
                control?.patchValue(val, { emitEvent: false });
            }
        }
        if (fd.filterVariantValues) {
            for (const [code, val] of Object.entries(fd.filterVariantValues)) {
                if (variantsGroup.contains(code)) {
                    variantsGroup.get(code)?.patchValue(val, { emitEvent: false });
                }
            }
        }
        this.loadSalesMandateFromFilterData();
        this.revalidateCompositionGroups();
    }

    private organizeFilters(filters: Filter[] = null, values: { [key: string]: unknown } = null): void {
        if (filters) {
            this.filters = filters;
        }
        this.displayFilters = this.filters.filter(
            (f) => (!f.canCreateVariant || !this.offerCanHaveVariants) && this.canShowFilter(f, values, 'display'),
        );
        this.variantFilters = this.filters.filter((f) => f.canCreateVariant && this.canShowFilter(f, values, 'variant'));

        // Update manual variant selection flag
        this.isUsingManualVariantSelection = this.hasOfferGroupFilter();

        this.filtersLoading = false;
        this.revalidateCompositionGroups();
    }

    hasVariantFilters() {
        return this.offerCanHaveVariants && this.variantFilters && this.variantFilters.length > 0;
    }

    hasOfferGroupFilter(): boolean {
        return this.offerCanHaveVariants && this.variantFilters.some((f) => f.canCreateOfferGroup);
    }

    onManualVariantSelection(selection: ManualVariantSelection): void {
        this.manualVariantSelectionTouched = true;
        this.manualVariantSelection = selection;
    }

    onManualVariantChange(result: VariantGenerationResult) {
        this.manualVariantResult = result;
        // Update form validity based on manual variant selection
        const variantsControl = this.form.get('variants') as FormGroup;
        if (result.isValid) {
            variantsControl.setErrors(null);
            // Also clear any validators that might be causing issues
            variantsControl.clearValidators();

            // For manual variant selection, we need to bypass the child control validation
            // by clearing errors and validators on all nested controls
            Object.keys(variantsControl.controls).forEach((controlKey) => {
                const nestedControl = variantsControl.get(controlKey);
                if (nestedControl) {
                    nestedControl.setErrors(null);
                    nestedControl.clearValidators();
                    nestedControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                }
            });

            variantsControl.updateValueAndValidity({ emitEvent: false });
        } else {
            variantsControl.setErrors({ manualVariantInvalid: true });
        }
    }

    private updateFilterVariantForms() {
        const variants = this.form.get('variants') as FormGroup;
        const oneVariantValidator = !this.canCreateMoreThanOneVariant() && this.hasVariantFilters();
        if (oneVariantValidator) {
            // If we have only one variant, we need to add the oneVariantValidator
            variants.setValidators([OneVariantValidator, VariantLengthValidator]);
        } else {
            variants.setValidators([VariantLengthValidator]);
        }
    }

    private canCreateMoreThanOneVariant(): boolean {
        return this.platform !== 'gazette' && this.user.pro;
    }

    canShowFilter(filter: Filter, values: { [key: string]: unknown }, mode: 'variant' | 'display'): boolean {
        if (this.shippable) {
            if (mode === 'variant' && (!filter.canCreateVariant || !this.offerCanHaveVariants)) {
                return false;
            }
            if (mode === 'display' && filter.canCreateVariant && this.offerCanHaveVariants) {
                return false;
            }
        }

        return this.toggleFilters(mode, filter, values);
    }

    private toggleFilters(mode: 'variant' | 'display', filter: Filter, values: { [key: string]: unknown }): boolean {
        const form: FormGroup =
            filter.canCreateVariant && this.offerCanHaveVariants
                ? (this.form.get('variants') as FormGroup)
                : (this.form.get('filters') as FormGroup);
        const code = filter.getCode();

        if (filter.canShow(values, this.filters)) {
            // Add control to form if not exists
            // Create the form control if it doesn't exist
            if (!form.contains(code)) {
                const control = mode === 'variant' ? filter.toVariantFormControl() : filter.toFormControl();
                if (filter.type === 'composition' && mode !== 'variant' && control instanceof FormGroup) {
                    this.seedCompositionLineControls(control, values?.[code]);
                }
                form.addControl(code, control, { emitEvent: false });
            }

            return true;
        }

        // If filter is not shown, we need to remove it from the form
        if (form.contains(code)) {
            form.removeControl(code, { emitEvent: false });
            // Remove the filter from the values
            delete values[code];
        }

        return false;
    }

    private seedCompositionLineControls(control: FormGroup, existing: unknown): void {
        for (const lineKey of listCompositionLineKeys(existing)) {
            if (control.contains(lineKey)) {
                continue;
            }
            const line = (existing as CompositionFilterValue)[lineKey];
            control.addControl(
                lineKey,
                new FormGroup({
                    value: new FormControl(line?.value ?? null, [Validators.required, Validators.minLength(1)]),
                    percentage: new FormControl(line?.percentage ?? 0, [Validators.required, Validators.min(1), Validators.max(100)]),
                }),
                { emitEvent: false },
            );
        }
    }

    private validateCompositionGroups = (control: AbstractControl): ValidationErrors | null => {
        const filtersGroup = control as FormGroup;
        const present = this.filters.filter((f) => f.type === 'composition' && filtersGroup.contains(f.getCode()));
        const availableFilterIds = this.filters.map((f) => f.id);
        const groups = groupCompositionFilters(present, availableFilterIds);
        let invalid = false;

        for (const [, groupFilters] of groups) {
            const percentageControls: AbstractControl[] = [];
            let total = 0;
            for (const filter of groupFilters) {
                const filterGroup = filtersGroup.get(filter.getCode()) as FormGroup | null;
                if (!filterGroup) {
                    continue;
                }
                for (const lineName of Object.keys(filterGroup.controls)) {
                    const percentageCtrl = filterGroup.get(lineName)?.get('percentage');
                    if (!percentageCtrl) {
                        continue;
                    }
                    percentageControls.push(percentageCtrl);
                    total += Number(percentageCtrl.value) || 0;
                }
            }
            const groupValid = total === 100;
            if (!groupValid) {
                invalid = true;
            }
            for (const percentageCtrl of percentageControls) {
                const errors = { ...(percentageCtrl.errors || {}) };
                if (groupValid) {
                    delete errors['invalidCompositionTotal'];
                } else {
                    errors['invalidCompositionTotal'] = true;
                }
                const next = Object.keys(errors).length ? errors : null;
                if (JSON.stringify(percentageCtrl.errors) !== JSON.stringify(next)) {
                    percentageCtrl.setErrors(next, { emitEvent: false });
                }
            }
        }

        return invalid ? { invalidCompositionTotal: true } : null;
    };

    private revalidateCompositionGroups(): void {
        this.form.get('filters')?.updateValueAndValidity({ emitEvent: false });
    }

    protected compositionGroupBannerFor(filter: Filter): { label: string; total: number; valid: boolean } | null {
        if (filter.type !== 'composition') {
            return null;
        }
        const availableFilterIds = this.filters.map((f) => f.id);
        const present = this.displayFilters.filter(
            (item) => item.type === 'composition' && this.hasFilterControl(this.getFilterGroupForm(), item.getCode()),
        );
        const key = compositionGroupKey(filter, availableFilterIds);
        const first = present.find((item) => compositionGroupKey(item, availableFilterIds) === key);
        if (!first || first.id !== filter.id) {
            return null;
        }
        const groupFilters = present.filter((item) => compositionGroupKey(item, availableFilterIds) === key);
        const total = compositionGroupTotal(groupFilters, this.getFilterGroupForm().value as Record<string, unknown>);
        return {
            label: compositionGroupLabel(key, groupFilters, this.filters),
            total,
            valid: total === 100,
        };
    }

    private enableSizeGuide() {
        if (!this.form.contains('sizeGuide')) {
            this.form.addControl('sizeGuide', new FormControl(null, [Validators.required, Validators.minLength(1)]));
        }
        this.refreshSizeGuideList();
    }

    protected refreshSizeGuideList(selectIdAfter?: number) {
        this.sizeGuideService.list().subscribe({
            next: (sizeGuides) => {
                this.sizeGuideList = sizeGuides.map((s) => ({
                    label: s.name,
                    value: '' + s.id,
                }));
                if (selectIdAfter != null) {
                    this.form.get('sizeGuide')?.setValue(['' + selectIdAfter]);
                }
            },
        });
    }

    async openNewSizeGuideModal() {
        const modal = await this.modalCtrl.create({
            component: SizeGuideCreateModalComponent,
            componentProps: { existingGuide: null },
            cssClass: 'modal-auto-height size-guide-create-modal',
        });
        await modal.present();
        const { data } = await modal.onWillDismiss<{ sizeGuide?: SizeGuide }>();
        if (data?.sizeGuide?.id) {
            this.refreshSizeGuideList(data.sizeGuide.id);
        }
    }

    private disableSizeGuide() {
        if (this.form.contains('sizeGuide')) {
            this.form.removeControl('sizeGuide');
        }
        this.sizeGuideList = null;
    }

    getFilterGroupForm(): FormGroup {
        return this.form.get('filters') as FormGroup;
    }

    getVariantsGroupForm(): FormGroup {
        return this.form.get('variants') as FormGroup;
    }

    countVariants() {
        if (this.isUsingManualVariantSelection && this.manualVariantResult) {
            return this.manualVariantResult.totalCount;
        }
        // We need to count the number of variants (combinations)
        return OfferUtils.countVariants(this.form.get('variants').value as string[][]);
    }

    hasFilterControl(form: FormGroup, code: string): boolean {
        return form.contains(code);
    }

    isInvalid(name: string): boolean {
        return this.form.get(name).invalid && this.form.get(name).touched;
    }

    private isFormValidForSubmission(): boolean {
        if (this.salesMandateRequired && !this.salesMandateMedia) {
            return false;
        }

        // For manual variant selection, we need custom validation logic
        if (this.isUsingManualVariantSelection) {
            // Check all form controls except variants
            const nonVariantControlsValid = Object.keys(this.form.controls)
                .filter((key) => key !== 'variants')
                .every((key) => {
                    const control = this.form.get(key);
                    return control.valid;
                });

            // Check manual variant selection validity
            const manualVariantsValid = this.manualVariantResult && this.manualVariantResult.isValid;

            return nonVariantControlsValid && manualVariantsValid;
        }

        // For automatic variant generation, use standard form validation
        return this.form.valid;
    }

    submit() {
        this.salesMandateTouched = true;
        // Check if form is valid, but with special handling for manual variant selection
        const isFormValid = this.isFormValidForSubmission();

        if (!isFormValid) {
            this.form.markAllAsTouched();
            if (this.isUsingManualVariantSelection && !this.manualVariantResult?.isValid) {
                this.offerGroupVariantSelector?.markAllAsTouched();
            }
            return;
        }

        let variants: OfferVariantPost[];
        let variantGroups: OfferVariantGroup[];

        if (this.isUsingManualVariantSelection && this.manualVariantResult) {
            // Use manual variant selection results
            if (!this.manualVariantResult.isValid) {
                return; // Don't submit if manual variants are invalid
            }
            variants = this.manualVariantResult.variants;
            variantGroups = this.manualVariantResult.variantGroups;
        } else {
            // Use automatic variant generation
            variants = this.generateVariants();
            variantGroups = this.variantGroups;
        }

        copySourceVariantIds(this.data.filterData?.variants, variants);
        this.reconcileKeptVariantDetails(variants, variantGroups ?? []);

        const filterData = new FilterData();
        filterData.name = this.form.get('name').value;
        filterData.description = this.form.get('description').value;
        filterData.sizeGuide = +this.form.get('sizeGuide')?.value[0];
        filterData.filterValues = this.form.get('filters').value;
        filterData.filterVariantValues = this.form.get('variants').value;
        filterData.manualVariantSelection = this.isUsingManualVariantSelection
            ? this.manualVariantSelectionTouched
                ? this.manualVariantSelection
                : this.data.filterData?.manualVariantSelection ?? null
            : null;
        filterData.filters = this.filters;
        filterData.variants = variants;
        filterData.variantGroups = variantGroups;
        filterData.documents = this.mergeOfferDocumentsForSubmit();

        this.data.filterData = filterData;
        this.nextStep.emit(this.data);
    }

    /**
     * Prix and photos are keyed by the same variant / group codes Details just regenerated.
     * Shared fields (VAT, packages, wholesale grid, photos with no group) stay where they are.
     */
    private reconcileKeptVariantDetails(variants: OfferVariantPost[] | undefined, variantGroups: OfferVariantGroup[]): void {
        const nextCodes = (variants ?? []).map((variant) => variant.code).filter((code): code is string => !!code);
        this.data.priceData.variants = keepPricedVariants(this.data.priceData?.variants, nextCodes);
        copyVariantGroupMedias(this.data.filterData?.variantGroups, variantGroups);
        if (this.isEdit) {
            attachEditMediasToOfferGroup(this.data.filterData?.variantGroups, variantGroups, this.data.mediaData?.medias);
        }
        if (variantGroups.length === 0) {
            return;
        }
        this.data.mediaData.medias = alignWizardMediasWithGroups(this.data.mediaData?.medias, variantGroups);
    }

    private generateVariants(): OfferVariantPost[] {
        this.variantGroups = [];
        const variantValues: { [key: string]: (string | { value: string })[] } = this.form.get('variants').value;
        if (!variantValues || Object.keys(variantValues).length === 0) {
            return;
        }
        const offerVariantGroups: {
            filter: Filter;
            values: OfferVariantValuePost[];
        }[] = [];

        for (const [key, values] of Object.entries(variantValues)) {
            const filterId = Filter.codeToId(key);
            const filter = this.filters.find((f) => f.id === filterId);
            const offerVariantGroup = {
                filter: filter,
                values: [] as OfferVariantValuePost[],
            };
            offerVariantGroups.push(offerVariantGroup);
            // If values are string, we need to convert them to filter values ids
            values.forEach((v) => {
                if (typeof v === 'string') {
                    const fv = filter.filterValues.find((fv) => fv.code === v);
                    offerVariantGroup.values.push(
                        new OfferVariantValuePost({
                            filterValue: fv?.id,
                            filterValueEntity: fv,
                        }),
                    );
                } else {
                    const raw = v.value;
                    // parseInt('', 10) is NaN, and typeof NaN === 'number'. Ignore blank and non-finite inputs.
                    if (typeof raw === 'string' && raw.trim() === '') {
                        return;
                    }
                    const value = filter.formatValue(raw);
                    if (typeof value === 'number') {
                        if (!Number.isFinite(value)) {
                            return;
                        }
                        offerVariantGroup.values.push(
                            new OfferVariantValuePost({
                                integerValue: value,
                            }),
                        );
                    } else if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
                        return;
                    } else {
                        offerVariantGroup.values.push(
                            new OfferVariantValuePost({
                                textValue: value,
                            }),
                        );
                    }
                }
            });
        }

        // Reorder the offer variant groups to have the one that can create offer groups first
        offerVariantGroups.sort((a, b) => {
            if (a.filter.canCreateOfferGroup && !b.filter.canCreateOfferGroup) {
                return -1;
            }
            if (!a.filter.canCreateOfferGroup && b.filter.canCreateOfferGroup) {
                return 1;
            }
            return 0;
        });

        const hasOfferGroups = offerVariantGroups.some((g) => g.filter.canCreateOfferGroup);
        // Use the first line to generate all combinations
        return this.generateVariantCombinationsLine(offerVariantGroups, hasOfferGroups);
    }

    private generateVariantCombinationsLine(
        offerVariantGroups: {
            filter: Filter;
            values: OfferVariantValuePost[];
        }[],
        hasGroups: boolean,
        currentDefinitions: PostOfferVariantDefinition[] = [],
        index: number = 0,
        variants: OfferVariantPost[] = [],
    ): OfferVariantPost[] {
        const line = offerVariantGroups[index];
        for (const value of line.values) {
            // If line is first and can create offer groups, we need to create an offer group for the value
            if (index === 0 && line.filter.canCreateOfferGroup) {
                const group: OfferVariantGroup = {
                    code: line.filter.getCode() + '_' + value.code,
                    filter: line.filter,
                    value: value,
                    variants: [],
                    medias: [],
                };
                this.variantGroups.push(group);
            }
            const definition = new PostOfferVariantDefinition({
                filter: line.filter.id,
                filterEntity: line.filter,
                value: value,
            });
            // Do we have a next line ?
            if (index < offerVariantGroups.length - 1) {
                this.generateVariantCombinationsLine(
                    offerVariantGroups,
                    hasGroups,
                    [...currentDefinitions, definition],
                    index + 1,
                    variants,
                );
            }
            // We have the last line, we can create the variant
            else {
                const variant = new OfferVariantPost();
                variant.definitions = [...currentDefinitions, definition];
                variants.push(variant);
                if (hasGroups) {
                    // Find filter
                    const groupDefinition = variant.definitions.find((d) => d.filterEntity.canCreateOfferGroup);
                    const code = groupDefinition.filterEntity.getCode() + '_' + groupDefinition.value.code;
                    // Find group with this code and add the variant
                    const group = this.variantGroups.find((g) => g.code === code);
                    if (group) {
                        group.variants.push(variant);
                    }
                }
            }
        }

        return variants;
    }
}
